import React from 'react';
import { Select, MenuItem } from '@mui/material';
import { colors } from 'Constants/Colors';
import { CustomSelectContainer, CustomSelectLabel } from './CustomSelect.styled';

let customSelectId = 0;

const getCustomSelectId = () => {
  customSelectId += 1;
  return `custom-select-${customSelectId}`;
};

interface Option {
  value: string | number;
  label: string;
}

interface Props {
  label?: string;
  /** Nombre accesible del control cuando no hay una etiqueta visible. */
  ariaLabel?: string;
  value?: string;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  placeholder?: string;
  options?: Option[];
  width?: string;
  disabled?: boolean;
}

const CustomSelect = ({
  label,
  ariaLabel,
  value,
  onChange,
  placeholder,
  options = [],
  width = 'fit-content',
  disabled = false,
}: Props) => {
  const selectId = React.useRef<string | undefined>(undefined);

  if (!selectId.current) {
    selectId.current = getCustomSelectId();
  }

  const labelId = `${selectId.current}-label`;
  const hasVisibleLabel = Boolean(label);

  return (
    <CustomSelectContainer style={{ width }}>
      <CustomSelectLabel id={hasVisibleLabel ? labelId : undefined} hidden={!hasVisibleLabel}>
        {label}
      </CustomSelectLabel>
      <Select
        id={selectId.current}
        labelId={hasVisibleLabel ? labelId : undefined}
        value={value === undefined || value === null ? '' : value}
        onChange={onChange}
        displayEmpty
        variant="standard"
        disabled={disabled}
        disableUnderline
        inputProps={{ 'aria-label': ariaLabel }}
        MenuProps={{
          PaperProps: {
            sx: {
              maxHeight: '300px',
              '& .MuiMenuItem-root': {
                color: 'black',
                height: '50px',
                display: 'flex',
                alignItems: 'center',
                padding: '0 16px',
                fontSize: '1rem',
                '&.Mui-selected': {
                  backgroundColor: `${colors.PRIMARY}20`,
                  '&:hover': {
                    backgroundColor: `${colors.PRIMARY}30`,
                  },
                },
                '&:hover': {
                  backgroundColor: `${colors.PRIMARY}10`,
                },
              },
            },
          },
        }}
        sx={{
          height: '50px',
          minWidth: '120px',
          '& .MuiSelect-select': {
            color: value !== null && value !== undefined ? 'black' : colors.GRAY,
            height: '50px !important',
            lineHeight: '50px',
            cursor: disabled ? 'not-allowed' : 'pointer',
            minWidth: 'auto',
            display: 'flex',
            alignItems: 'center',
            fontSize: '1rem',
            background: 'transparent !important',
            paddingTop: 0,
            paddingBottom: 0,
            paddingRight: '24px',
          },
          '& .MuiSelect-icon': {
            color: colors.PRIMARY,
            top: 'calc(50% - 12px)',
          },
        }}
        renderValue={selected => {
          if (selected === '') {
            return <span style={{ color: colors.GRAY_LIGHT }}>{placeholder}</span>;
          }
          const selectedOption = options.find(opt => opt.value === selected);
          return selectedOption?.label || selected;
        }}
      >
        {options.map(option => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </Select>
    </CustomSelectContainer>
  );
};

export default CustomSelect;
